import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { useSelector } from "react-redux";

const FunnelViewChart = () => {
  const pid = useSelector((state) => state.user.projectId);

  const [orgOptions, setOrgOptions] = useState([]);
  const [schemeOptions, setSchemeOptions] = useState([]);
  const [schemeTypeOptions, setSchemeTypeOptions] = useState([]);
  const [caseDistrictOptions, setCaseDistrictOptions] = useState([]);

  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [selectedSchemes, setSelectedSchemes] = useState([]);
  const [selectedSchemeTypes, setSelectedSchemeTypes] = useState([]);
  const [selectedCaseDistrict, setSelectedCaseDistrict] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [appliedFilters, setAppliedFilters] = useState({
    orgs: [],
    schemes: [],
    schemeTypes: [],
    caseDistricts: [],
    startDate: '',
    endDate: ''
  });

  const [chartData, setChartData] = useState([]);
  const [metricsData, setMetricsData] = useState([]);
  const [schemeBenefit, setSchemeBenefit] = useState(0);
  const [loading, setLoading] = useState(false);

  const schemeTypeLabelMap = {
    sch: "Schemes",
    doc: "Document",
    dfl: "DFL",
    yc: "Yojana Card"
  };

  const mergeOptions = (selected, fetched) => {
    const map = new Map();
    [...selected, ...fetched].forEach(opt => map.set(opt.value, opt));
    return Array.from(map.values());
  };

  const getQueryParams = () => {
    const params = new URLSearchParams();

    appliedFilters.schemeTypes.forEach(type => params.append("SchemeType", type));
    appliedFilters.orgs.forEach(org => params.append("OrgName", org));
    appliedFilters.schemes.forEach(scheme => params.append("SchemeName", scheme));
    appliedFilters.caseDistricts.forEach(d => params.append("CaseDistrict", d));

    if (pid) params.append("pid", pid);
    if (appliedFilters.startDate) params.append("start_date", appliedFilters.startDate);
    if (appliedFilters.endDate) params.append("end_date", appliedFilters.endDate);

    return params.toString();
  };

  const fetchFilterOptions = async () => {
    try {
      const params = getQueryParams();

      const [orgRes, schemeRes, schemeTypeRes, districtRes] = await Promise.all([
        fetch(`https://api-project360-insights.haqdarshak.com/kpi/waterfall/unique-org/?${params}`),
        fetch(`https://api-project360-insights.haqdarshak.com/kpi/waterfall/unique-scheme/?${params}`),
        fetch(`https://api-project360-insights.haqdarshak.com/kpi/waterfall/unique-scheme-type/?${params}`),
        fetch(`https://api-project360-insights.haqdarshak.com/kpi/waterfall/unique-case-districts/?${params}`)
      ]);

      const orgData = await orgRes.json();
      const schemeData = await schemeRes.json();
      const schemeTypeData = await schemeTypeRes.json();
      const districtData = await districtRes.json();

      const orgOpts = orgData.unique_Organizations.map(o => ({ value: o, label: o }));
      const schemeOpts = schemeData.unique_Schemes.map(s => ({ value: s, label: s }));
      const schemeTypeOpts = schemeTypeData.unique_Scheme_Types.map(t => ({
        value: t,
        label: schemeTypeLabelMap[t] || t
      }));
      const districtOpts = districtData.unique_case_districts.map(d => ({
        value: d,
        label: d
      }));

      setOrgOptions(prev => mergeOptions(prev, orgOpts));
      setSchemeOptions(prev => mergeOptions(prev, schemeOpts));
      setSchemeTypeOptions(prev => mergeOptions(prev, schemeTypeOpts));
      setCaseDistrictOptions(prev => mergeOptions(prev, districtOpts));

    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  };

  const fetchChartData = async () => {
    setLoading(true);
    try {
      const params = getQueryParams();

      const isDateFilterApplied = appliedFilters.startDate || appliedFilters.endDate;

      const apiUrl = `https://api-project360-insights.haqdarshak.com/kpi/view/funnel/chart?${params}`;

      const chartRes = await fetch(apiUrl);
      const data = await chartRes.json();

      const open = data.application_open || 0;
      const ds = data.docket_submitted || 0;
      const br = data.br_count || 0;
      const aborted = data.aborted_count || 0;
      const rejected=data.rejected_count || 0

      const totalCases = open + ds + br + aborted+rejected;

      const metrics = [
        {
          name: "Total Cases",
          value: totalCases,
          percent: 100,
          fill: "#1E3A8A"
        },
        {
          name: "Pending in Open",
          value: open,
          percent: totalCases ? ((open / totalCases) * 100).toFixed(1) : 0,
          fill: "#CF87C4"
        },
        {
          name: "Pending in DS",
          value: ds,
          percent: totalCases ? ((ds / totalCases) * 100).toFixed(1) : 0,
          fill: "#CF87C4"
        },
        {
          name: "Reached BR",
          value: br,
          percent: totalCases ? ((br / totalCases) * 100).toFixed(1) : 0,
          fill: "#CF87C4"
        },
        {
          name: "Aborted Count",
          value: aborted,
          percent: totalCases ? ((aborted / totalCases) * 100).toFixed(1) : 0,
          fill: "#CF87C4"
        },
        {
          name: "Application Rejected Count",
          value: rejected,
          percent: totalCases ? ((rejected / totalCases) * 100).toFixed(1) : 0,
          fill: "#CF87C4"
        },

      ];

      setMetricsData(metrics);
      setChartData(metrics);

      const benefitRes = await fetch(`https://api-project360-insights.haqdarshak.com/kpi/scheme-benefits/?${params}`);
      const benefitData = await benefitRes.json();
      setSchemeBenefit(benefitData.br_value || 0);

    } catch (error) {
      console.error("Error fetching chart data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFilterOptions();
    fetchChartData();
  }, [appliedFilters, pid]);

  const applyFilters = () => {
    setAppliedFilters({
      orgs: selectedOrgs,
      schemes: selectedSchemes,
      schemeTypes: selectedSchemeTypes,
      caseDistricts: selectedCaseDistrict,
      startDate,
      endDate
    });
  };

  const clearFilters = () => {
    setSelectedOrgs([]);
    setSelectedSchemes([]);
    setSelectedSchemeTypes([]);
    setSelectedCaseDistrict([]);
    setStartDate('');
    setEndDate('');

    setAppliedFilters({
      orgs: [],
      schemes: [],
      schemeTypes: [],
      caseDistricts: [],
      startDate: '',
      endDate: ''
    });
  };

  const MultiSelectDropdown = ({ options, selected, setSelected, placeholder }) => (
    <Select
      isMulti
      options={options}
      value={options.filter(opt => selected.includes(opt.value))}
      onChange={(selectedOptions) => setSelected(selectedOptions.map(o => o.value))}
      placeholder={placeholder}
      closeMenuOnSelect={false}
    />
  );

  return (
    <div className="max-w-[90vw] ml-[3vw] mt-[3vh] border-2 border-gray-300 shadow-md rounded-md bg-white">

      <div className="px-6 py-4 border-b">
        <h2 className="text-xl font-semibold text-pink-800">Funnel View</h2>
      </div>

      <div className="px-6 py-4 grid md:grid-cols-3 gap-4">

        <div className="flex flex-col">
          <label className="text-sm font-medium text-gray-600 mb-1">Select State-Org Name</label>
          <MultiSelectDropdown options={orgOptions} selected={selectedOrgs} setSelected={setSelectedOrgs} placeholder="Organization" />
        </div>

        <div className="flex flex-col">
          <label className="text-sm font-medium text-gray-600 mb-1">Select Service Type</label>
          <MultiSelectDropdown options={schemeTypeOptions} selected={selectedSchemeTypes} setSelected={setSelectedSchemeTypes} placeholder="Service Type" />
        </div>

        <div className="flex flex-col">
          <label className="text-sm font-medium text-gray-600 mb-1">Select Scheme Name</label>
          <MultiSelectDropdown options={schemeOptions} selected={selectedSchemes} setSelected={setSelectedSchemes} placeholder="Scheme Name" />
        </div>

        <div className="flex flex-col">
          <label className="text-sm font-medium text-gray-600 mb-1">Select Case District</label>
          <MultiSelectDropdown options={caseDistrictOptions} selected={selectedCaseDistrict} setSelected={setSelectedCaseDistrict} placeholder="Case District" />
        </div>

        <div className="flex flex-col">
          <label className="text-sm font-medium text-gray-600 mb-1">Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            className="border p-2 rounded h-[38px]"
          />
        </div>

        <div className="flex flex-col">
          <label className="text-sm font-medium text-gray-600 mb-1">End Date</label>
          <input
            type="date"
            value={endDate}
            min={startDate}
            onChange={e => setEndDate(e.target.value)}
            className="border p-2 rounded h-[38px]"
          />
        </div>

        <div className="flex gap-3">
          <button onClick={clearFilters} className="px-4 py-2 bg-gray-200  hover:bg-gray-300 transition disabled:opacity-50 rounded">
            Clear Filters
          </button>
          <button onClick={applyFilters} className="px-4 py-2 bg-blue-700 text-white rounded">
            Apply Filters
          </button>
        </div>
      </div>

      <div className="p-6 grid grid-cols-1 md:grid-cols-6 gap-4 bg-gray-50">
        {loading ? (
          <div>Loading...</div>
        ) : (
          metricsData.map((item, i) => (
            <div key={i} className="bg-white p-4 rounded shadow border">
              <p className="text-xs text-gray-500">{item.name}</p>

              <p className="text-xl font-bold" style={{ color: item.fill }}>
                {item.value?.toLocaleString()}
              </p>

              {item.name !== "Total Cases" && (
                <p className="text-xs text-gray-500 mt-1">
                  {item.percent}% of Total
                </p>
              )}
            </div>
          ))
        )}
      </div>

    </div>
  );
};

export default FunnelViewChart;